import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import pick from 'lodash.pick'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tooltip
} from '@material-ui/core'
import { Close, Save } from '@material-ui/icons'
import TextField from '../components/TextField.js'
import redux from '../redux/index.js'
import {
  getFileExtensionFromHost,
  emailFileNameOutlookReadMode,
  getEmailMetadataReadMode,
  getOpenDocumentDefaultNameWithSource,
  isOffice,
  isOutlook,
  deleteDocumentProperty
} from '../lib/office.js'
import { formatResourceString, getFileExtension, PDF_CONVERTIBLE_FILES } from '../util.js'
import {
  CONTENT_BACKGROUND,
  FOOTER_BACKGROUND,
  FOOTER_HEIGHT,
  HEADER_BACKGROUND,
  HEADER_HEIGHT
} from '../styles.js'

import Choice from './fields/Choice.js'
import ContactChoice from './fields/ContactChoice.js'
import MultiChoice from './fields/MultiChoice.js'
import Note from './fields/Note.js'
import Text from './fields/Text.js'

const getFieldComponent = (ct) => {
  const type = ct.ui_type || ct.field_type_as_string
  switch (type) {
    case 'Choice':
      return Choice
    case 'ContactChoice':
      return ContactChoice
    case 'MultiChoice':
      return MultiChoice
    case 'Note':
      return Note
    case 'Text':
      return Text
    default:
      console.log('SaveDialog: unknown component for type', type)
      return null
  }
}

const SaveDialog = ({
  strings,
  selectedSiteItem,
  selectedFileNode,
  droppedFiles,
  fileName,
  savePdfCopy,
  contentTypes,
  properties,
  onHideSaveDialog,
  onSetFileName,
  onSetProperty,
  onSetSavePdfCopy,
  onSaveFile,
  getData
}) => {
  const [overwriteConfirmDialog, setOverwriteConfirmDialog] = useState({
    open: false,
    fileName: ''
  })
  const overwriteConfirmResolverRef = useRef(null)

  useEffect(() => getData(selectedSiteItem?.type, droppedFiles), [])

  useEffect(() => {
    return () => {
      if (typeof overwriteConfirmResolverRef.current === 'function') {
        overwriteConfirmResolverRef.current(false)
        overwriteConfirmResolverRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const outlookContext = isOutlook()

    const key = 'pdscol_document_class'
    const currentValue = String(properties?.[key] || '').trim()
    if (currentValue.length > 0) {
      return
    }

    const documentClassField = (contentTypes || []).find((ct) => ct.field_internal_name === key)
    const options = getDocumentClassChoices(documentClassField)
    if (options.length === 0) {
      return
    }

    const sharePointDefault = resolveDocumentClassDefault(documentClassField, options)
    if (sharePointDefault) {
      console.info('save-dialog document class default source: sharepoint', { value: sharePointDefault })
      onSetProperty(key, sharePointDefault)
      return
    }

    if (outlookContext) {
      const fallbackValue = options.find((option) => {
        const normalized = String(option || '').trim().toLowerCase()
        return normalized === 'e-post' || normalized === 'epost' || normalized === 'e-mail' || normalized === 'email'
      })
      if (fallbackValue) {
        console.info('save-dialog document class default source: outlook-fallback', { value: fallbackValue })
        onSetProperty(key, fallbackValue)
      }
    }
  }, [contentTypes, properties, onSetProperty])

  useEffect(() => {
    let active = true

    const setDefaultFileName = async () => {
      if (String(fileName || '').trim().length > 0) {
        console.info('save-dialog default filename source: existing-state', { fileName })
        return
      }

      const selectedNodeName = getDefaultSaveDialogFileName(selectedFileNode)
      if (selectedNodeName.length > 0) {
        console.info('save-dialog default filename source: selected-file-node', {
          nodeType: selectedFileNode?.type,
          fileName: selectedNodeName
        })
        onSetFileName(selectedNodeName)
        return
      }

      const { name: officeDocumentName, source } = await getOpenDocumentDefaultNameWithSource()
      if (active && String(fileName || '').trim().length === 0 && officeDocumentName.length > 0) {
        console.info(`save-dialog default filename source: ${source}`, { fileName: officeDocumentName })
        onSetFileName(officeDocumentName)
      } else if (active) {
        console.info('save-dialog default filename source: none')
      }
    }

    setDefaultFileName()

    return () => {
      active = false
    }
  }, [fileName, onSetFileName, selectedFileNode])

  const resolvedFileName = getResolvedFileName(fileName)
  const relativeFilePath = getSaveDialogRelativeFilePath(selectedFileNode, resolvedFileName)
  const pdfConvertible = isSharePointPdfConvertible(resolvedFileName)
  const savePdfLabel = strings.save_pdf_copy || 'Save copy as PDF'
  const supportedTypesLabel = getSupportedPdfFileTypesLabel()
  const unsupportedTooltip = formatResourceString(
    strings.save_pdf_copy_unsupported_tooltip || 'Not available for this file type. Supported file types: %1',
    supportedTypesLabel
  )

  useEffect(() => {
    if (!pdfConvertible && savePdfCopy) {
      onSetSavePdfCopy(false)
    }
  }, [pdfConvertible, savePdfCopy, onSetSavePdfCopy])

  const isSaveDisabled = () => {
    if (fileName.length === 0) return true
    if (isOffice() || isOutlook()) {
      return contentTypes
        .filter(ct => ct.ui_required)
        .some(ct => {
          const key = ct.field_internal_name
          return !properties[key]
        })
    } else {
      return false
    }
  }

  const renderFields = () => {
    if (isOffice() || isOutlook()) {
      return contentTypes.map(ct => {
        const Field = getFieldComponent(ct)
        return Field ? <Field style={{ marginBottom: '10px' }} key={ct.field_id} contentType={ct} /> : null
      })
    } else {
      return null
    }
  }

  const requestOverwriteConfirmation = (nextFileName) => {
    return new Promise((resolve) => {
      overwriteConfirmResolverRef.current = resolve
      setOverwriteConfirmDialog({
        open: true,
        fileName: String(nextFileName || '')
      })
    })
  }

  const resolveOverwriteConfirmation = (confirmed) => {
    if (typeof overwriteConfirmResolverRef.current === 'function') {
      overwriteConfirmResolverRef.current(!!confirmed)
      overwriteConfirmResolverRef.current = null
    }
    setOverwriteConfirmDialog({
      open: false,
      fileName: ''
    })
  }

  const disableSave = isSaveDisabled()

  const renderOverwriteConfirmDialog = () => {
    const nextFileName = String(overwriteConfirmDialog.fileName || '').trim()
    const message = formatResourceString(
      strings.overwrite_file_confirm || 'The file "%1" already exists. Click Overwrite to replace it or Abort to cancel.',
      nextFileName
    )

    return (
      <Dialog
        open={overwriteConfirmDialog.open}
        onClose={() => resolveOverwriteConfirmation(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle style={overwriteTitleStyle}>{strings.overwrite_dialog_title || 'Overwrite file?'}</DialogTitle>
        <DialogContent style={overwriteContentStyle}>
          <div style={overwriteWarningStyle}>{message}</div>
        </DialogContent>
        <DialogActions style={overwriteActionsStyle}>
          <Button onClick={() => resolveOverwriteConfirmation(false)} style={overwriteAbortActionStyle}>
            <Close style={actionIconStyle} />
            {strings.abort_action || strings.cancel || 'Abort'}
          </Button>
          <Button onClick={() => resolveOverwriteConfirmation(true)} style={overwriteConfirmActionStyle}>
            <Save style={actionIconStyle} />
            {strings.overwrite_action || 'Overwrite'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog fullScreen open>
        <DialogTitle style={titleStyle}>
          <div style={titleContentStyle}>
            {strings.save_dialog_title}
          </div>
        </DialogTitle>
        <DialogContent style={contentStyle}>
          <div style={infoPathBoxStyle}>
            <span style={infoPathTextStyle}>{relativeFilePath || strings.no_options}</span>
          </div>
          <TextField
            required
            label={strings.filename}
            value={fileName}
            style={{ background: 'white', marginBottom: '10px' }}
            onChange={(event) => onSetFileName(event.target.value)}
          />
          <Tooltip
            title={pdfConvertible ? '' : unsupportedTooltip}
            arrow
            disableHoverListener={pdfConvertible}
            disableFocusListener={pdfConvertible}
            disableTouchListener={pdfConvertible}
          >
            <span>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={savePdfCopy && pdfConvertible}
                    disabled={!pdfConvertible}
                    onChange={(event) => onSetSavePdfCopy(event.target.checked)}
                    color='primary'
                  />
                )}
                label={savePdfLabel}
                style={{ marginBottom: '10px' }}
              />
            </span>
          </Tooltip>
          {renderFields()}
        </DialogContent>
        <DialogActions style={actionsStyle}>
          <Button
            onClick={onHideSaveDialog}
            style={actionButtonStyle()}
          >
            <Close style={actionIconStyle} />{strings.cancel}
          </Button>
          <Button
            disabled={disableSave}
            onClick={() => onSaveFile(
              contentTypes,
              properties,
              selectedSiteItem,
              selectedFileNode,
              fileName,
              savePdfCopy && pdfConvertible,
              droppedFiles,
              requestOverwriteConfirmation
            )}
            style={actionButtonStyle(disableSave)}
          >
            <Save style={actionIconStyle} />{strings.save}
          </Button>
        </DialogActions>
      </Dialog>
      {renderOverwriteConfirmDialog()}
    </>
  )
}

const titleStyle = {
  background: HEADER_BACKGROUND,
  color: 'white',
  padding: '0px 19px',
  fontSize: '20px',
  height: HEADER_HEIGHT
}

const titleContentStyle = {
  marginTop: '8px'
}

const contentStyle = {
  background: CONTENT_BACKGROUND
}

const infoPathBoxStyle = {
  background: '#f4f7f9',
  border: '1px solid #d4dde3',
  borderRadius: 4,
  color: '#2b3f4c',
  fontSize: '0.8rem',
  lineHeight: 1.3,
  marginBottom: 10,
  padding: '8px 10px'
}

const infoPathTextStyle = {
  wordBreak: 'break-word'
}

const actionsStyle = {
  borderTop: '1px solid white',
  background: FOOTER_BACKGROUND,
  height: FOOTER_HEIGHT,
  padding: 0
}

const overwriteTitleStyle = {
  color: '#2b3f4c',
  fontSize: '1rem',
  fontWeight: 600
}

const overwriteContentStyle = {
  paddingTop: 8
}

const overwriteWarningStyle = {
  color: '#7a2f00',
  background: '#fde8d8',
  border: '1px solid #f0bf9a',
  borderRadius: 4,
  padding: '8px 10px',
  fontSize: '0.85rem',
  lineHeight: 1.35
}

const overwriteActionsStyle = {
  padding: '6px 10px 10px'
}

const overwriteAbortActionStyle = {
  textTransform: 'none',
  color: '#455560'
}

const overwriteConfirmActionStyle = {
  textTransform: 'none',
  color: '#0f5ea6'
}

export const actionButtonStyle = (disabled = false) => {
  return {
    color: disabled ? '#aaafb2' : 'white',
    textTransform: 'none',
    marginRight: '10px'
  }
}

export const actionIconStyle = {
  marginRight: '5px'
}

const mapStateToProps = (state) => {
  return {
    strings: state.locale.strings,
    selectedSiteItem: state.selected.current,
    selectedFileNode: state.files.selectedFileNode,
    droppedFiles: Array.isArray(state.dialog.meta?.droppedFiles) ? state.dialog.meta.droppedFiles : [],
    fileName: state.save.fileName,
    savePdfCopy: state.save.savePdfCopy,
    contentTypes: state.contentType.items,
    properties: state.save.documentProperties
  }
}

const mapDispatchToProps = (dispatch) => {
  const { contentType, dialog, files, save } = redux.actions
  return {
    getData: (uiContext, droppedFiles) => {
      dispatch(contentType.getContentTypes(uiContext))
      dispatch(save.setSavePdfCopy(false))
      dispatch(save.setSaveDialogFileName(''))
      if (Array.isArray(droppedFiles) && droppedFiles.length > 0) {
        const droppedFileName = getDroppedSaveDialogFileName(droppedFiles[0])
        if (droppedFileName.length > 0) {
          dispatch(save.setSaveDialogFileName(droppedFileName))
          return
        }
      }
      if (isOutlook()) {
        dispatch(save.setSaveDialogFileName(emailFileNameOutlookReadMode()))
      }
    },
    onHideSaveDialog: () => {
      dispatch(save.setSaveDialogFileName(''))
      dispatch(dialog.hideDialog())
    },
    onSaveFile: async (contentTypes, properties, siteItem, fileItem, fileName, savePdfCopy, droppedFiles, onOverwriteConflict) => {
      const dropped = Array.isArray(droppedFiles) ? droppedFiles.filter(Boolean) : []
      const filteredNames = contentTypes.map(ct => ct.field_internal_name)
      const xmlProperties = pick(properties, filteredNames)

      if (dropped.length > 0) {
        const primary = dropped[0]
        const resolvedName = cleanDroppedFileName(fileName, primary?.name)
        dispatch(save.uploadDroppedFiles(siteItem, fileItem, [{ file: primary, fileName: resolvedName }], xmlProperties, () => {
          const { type } = fileItem
          if (type === 'rootfolder') {
            dispatch(files.getRootFolder(siteItem))
          } else if (type === 'folder') {
            dispatch(files.getSubFolder(siteItem, fileItem))
          }
          dispatch(dialog.hideDialog())
        }, {
          overwrite: false,
          onOverwriteConflict
        }))
        return
      }

      // If document property exists, remove it before trying to save.
      if (isOffice()) {
        try {
          await deleteDocumentProperty('pds_id')
          await deleteDocumentProperty('pds_type')
        } catch (e) {
          console.warn('failed to remove document property', e)
        }
      }

      dispatch(save.uploadFile(siteItem, fileItem, fileName, xmlProperties, savePdfCopy, () => {
        const { type } = fileItem
        if (type === 'rootfolder') {
          dispatch(files.getRootFolder(siteItem))
        } else if (type === 'folder') {
          dispatch(files.getSubFolder(siteItem, fileItem))
        }
        dispatch(dialog.hideDialog())
      }, {
        overwrite: false,
        onOverwriteConflict
      }))
    },
    onSetFileName: (fileName) => {
      dispatch(save.setSaveDialogFileName(fileName))
    },
    onSetProperty: (key, value) => {
      dispatch(save.setProperty(key, value))
    },
    onSetSavePdfCopy: (savePdfCopy) => {
      dispatch(save.setSavePdfCopy(savePdfCopy))
    }
  }
}

function cleanDroppedFileName (value, fallbackName = '') {
  const preferred = String(value || '').trim()
  if (preferred.length > 0) {
    return preferred
  }
  return String(fallbackName || '').trim()
}

function getDroppedSaveDialogFileName (file) {
  const trimmed = String(file?.name || '').trim()
  if (!trimmed.length) {
    return ''
  }

  const ext = getFileExtension(trimmed)
  if (ext !== 'eml' && ext !== 'msg') {
    return trimmed
  }

  const suffix = `.${ext}`
  const baseName = trimmed.toLowerCase().endsWith(suffix)
    ? trimmed.slice(0, -suffix.length)
    : trimmed
  return `${baseName} ${formatDateForFileName(resolveDroppedEmailDateForDialog(file))}.${ext}`
}

function formatDateForFileName (date) {
  const resolved = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date()
  const yyyy = resolved.getFullYear()
  const mm = String(resolved.getMonth() + 1).padStart(2, '0')
  const dd = String(resolved.getDate()).padStart(2, '0')
  const hh = String(resolved.getHours()).padStart(2, '0')
  const min = String(resolved.getMinutes()).padStart(2, '0')
  const ss = String(resolved.getSeconds()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}-${min}-${ss}`
}

function resolveDroppedFileDate (file) {
  const millis = Number(file?.lastModified)
  if (Number.isFinite(millis) && millis > 0) {
    return new Date(millis)
  }

  if (file?.lastModifiedDate instanceof Date && !Number.isNaN(file.lastModifiedDate.getTime())) {
    return file.lastModifiedDate
  }

  if (isOutlook()) {
    const fallback = parseDateLike(getEmailMetadataReadMode()?.dateTimeCreated)
    if (fallback) {
      return fallback
    }
  }

  return new Date()
}

function resolveDroppedEmailDateForDialog (file) {
  if (isOutlook()) {
    const fallback = parseDateLike(getEmailMetadataReadMode()?.dateTimeCreated)
    if (fallback) {
      return fallback
    }
  }

  return resolveDroppedFileDate(file)
}

function parseDateLike (value) {
  if (!value) {
    return null
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value
  }

  const text = String(value).trim()
  const isoCandidate = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)
    ? text.replace(' ', 'T')
    : text

  const parsed = new Date(isoCandidate)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed
}

function getResolvedFileName (fileName) {
  const clean = String(fileName || '').trim()
  const hostExt = getFileExtensionFromHost()
  if (!clean.length || !hostExt) {
    return clean
  }

  const currentExt = getFileExtension(clean)
  if (currentExt === hostExt) {
    return clean
  }

  return `${clean}.${hostExt}`
}

function getDefaultSaveDialogFileName (selectedFileNode) {
  if (selectedFileNode?.type !== 'file') {
    return ''
  }

  const selectedName = String(selectedFileNode?.name || '').trim()
  if (selectedName.length > 0) {
    return selectedName
  }

  const selectedTitle = String(selectedFileNode?.title || '').trim()
  if (selectedTitle.length > 0) {
    return selectedTitle
  }

  return ''
}

function isSharePointPdfConvertible (fileName) {
  const ext = getFileExtension(String(fileName || ''))
  return PDF_CONVERTIBLE_FILES.includes(ext)
}

function getSupportedPdfFileTypesLabel () {
  const uniqueTypes = Array.from(new Set(PDF_CONVERTIBLE_FILES))
  uniqueTypes.sort()
  return uniqueTypes.map((ext) => `.${ext}`).join(', ')
}

function getDocumentClassChoices (field) {
  const choices = field?.field_blob?.choice?.choices
  return Array.isArray(choices)
    ? choices.filter((value) => String(value || '').trim().length > 0)
    : []
}

function resolveDocumentClassDefault (field, options) {
  if (!field || options.length === 0) {
    return ''
  }

  const candidates = [
    field.default_value,
    field.field_default_value,
    field.ui_default_value,
    field.default,
    field.field_blob?.default,
    field.field_blob?.defaultValue,
    field.field_blob?.choice?.default,
    field.field_blob?.choice?.defaultValue,
    field.field_blob?.choice?.default_choice,
    field.field_blob?.choice?.selected,
    field.field_blob?.choice?.selectedValue
  ]

  for (const candidate of candidates) {
    const defaultValue = String(candidate || '').trim()
    if (!defaultValue.length) {
      continue
    }

    const match = options.find((option) => String(option || '').trim() === defaultValue)
    if (match) {
      return match
    }
  }

  return ''
}

function getSaveDialogRelativeFilePath (selectedFileNode, fileName) {
  const basePath = getDocumentLibraryRelativePath(selectedFileNode)
  const nextName = String(fileName || '').trim()

  if (!basePath && !nextName) {
    return ''
  }

  if (selectedFileNode?.type === 'file') {
    const filePath = replaceLastRelativePathElement(basePath, nextName)
    return formatInfoBoxRelativePath(filePath, { trailingSlash: false })
  }

  const joined = joinRelativePathElements(basePath, nextName)
  return formatInfoBoxRelativePath(joined, { trailingSlash: false })
}

function getDocumentLibraryRelativePath (item) {
  if (typeof item?.path === 'string' && item.path.length > 0) {
    return normalizeDocumentLibraryPath(item.path)
  }

  const value = item?.serverRelativeUrl || item?.uri
  if (typeof value !== 'string' || value.length === 0) {
    return ''
  }

  try {
    const path = value.startsWith('http') ? new URL(value).pathname : value
    return normalizeDocumentLibraryPath(decodeURIComponent(path))
  } catch (err) {
    return normalizeDocumentLibraryPath(String(value))
  }
}

function normalizeDocumentLibraryPath (value) {
  const trimmed = String(value || '').replace(/^\/+/, '')
  if (!trimmed) {
    return ''
  }

  const segments = trimmed.split('/').filter(Boolean)
  const filesIndex = segments.findIndex((segment) => /^files(?:_.+)?$/i.test(segment))
  if (filesIndex >= 0) {
    return segments.slice(filesIndex + 1).join('/')
  }

  return trimmed
}

function joinRelativePathElements (basePath, nextElement) {
  const base = String(basePath || '').replace(/^\/+|\/+$/g, '')
  const next = String(nextElement || '').trim().replace(/^\/+|\/+$/g, '')

  if (!base) {
    return next
  }
  if (!next) {
    return base
  }

  return `${base}/${next}`
}

function replaceLastRelativePathElement (path, nextLeaf) {
  const normalizedPath = String(path || '').replace(/^\/+|\/+$/g, '')
  const normalizedLeaf = String(nextLeaf || '').trim().replace(/^\/+|\/+$/g, '')
  if (!normalizedPath) {
    return normalizedLeaf
  }

  const segments = normalizedPath.split('/').filter(Boolean)
  if (segments.length === 0) {
    return normalizedLeaf
  }

  if (!normalizedLeaf) {
    return normalizedPath
  }

  segments[segments.length - 1] = normalizedLeaf
  return segments.join('/')
}

function formatInfoBoxRelativePath (value, options = {}) {
  const trimmed = String(value || '').trim()
  const withoutEdges = trimmed.replace(/^\/+|\/+$/g, '')
  const trailingSlash = options.trailingSlash !== false
  if (!withoutEdges) {
    return '/'
  }

  const elements = withoutEdges.split('/').filter(Boolean)
  const joined = `/${elements.join('/')}`
  return trailingSlash ? `${joined}/` : joined
}

export default connect(mapStateToProps, mapDispatchToProps)(SaveDialog)
